import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NodeDetailsSidepanelContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {
  TaskNodeDetailsSidepanelContentProps,
  SidepanelTab,
  NodeSettingsTabKeys,
} from '../processFlowchartConfigEditorConstants';
import {isInteractiveTaskNode, TaskSchemaNode} from '../processFlowchartConfigEditorNodeTypes';

function filterTabsForSelectedNode(
  tabs: Array<SidepanelTab>,
  selectedNode: TaskSchemaNode
): Array<SidepanelTab> {
  return tabs.filter((tab) => {
    if (
      tab.key === NodeSettingsTabKeys.FLOWCHART_SETTINGS &&
      selectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    ) {
      // There are no transitions into the root node so the FLOWCHART_SETTINGS tab is not relevant.
      return false;
    }
    return true;
  });
}

export default function TaskNodeDetailsSidepanelContent(props: {
  nodeDetailsSidepanelContentProps: TaskNodeDetailsSidepanelContentProps;
  tabs: Array<SidepanelTab>;
}): JSX.Element {
  const {nodeDetailsSidepanelContentProps, tabs} = props;
  const {selectedNode} = NodeDetailsSidepanelContext.useContext();
  if (!isInteractiveTaskNode(selectedNode)) {
    assert(false, 'Expected selected node to be a task node');
  }
  const visibleTabs = filterTabsForSelectedNode(tabs, selectedNode);
  const defaultTabKey = visibleTabs[0].key;
  const nodeFieldDetailsTab = visibleTabs.find((tab) => tab.openOnFieldSelect);

  const [activeTab, setActiveTab] = useState<string>(
    nodeDetailsSidepanelContentProps.selectedFieldId
      ? (nodeFieldDetailsTab?.key ?? defaultTabKey)
      : defaultTabKey
  );

  useEffect(() => {
    const newActiveTab = nodeDetailsSidepanelContentProps.selectedFieldId
      ? (nodeFieldDetailsTab?.key ?? defaultTabKey)
      : defaultTabKey;
    setActiveTab(newActiveTab);
  }, [nodeDetailsSidepanelContentProps.selectedFieldId, nodeFieldDetailsTab?.key, defaultTabKey]);

  if (visibleTabs.length === 1) {
    const tab = visibleTabs[0];
    return <tab.content {...nodeDetailsSidepanelContentProps} />;
  } else {
    return (
      <TabContainer<string>
        activeTab={activeTab}
        onRequestTabChange={setActiveTab}
        className={classNames(css(styles.sidepanelTabContainerStyle))}
        headerDividerClassName={css(styles.sidepanelHeaderDividerStyle)}
      >
        {visibleTabs.map((tab) => (
          <Tab
            key={tab.key}
            title={<div className={css(styles.tabTitleStyle)}>{tab.label}</div>}
            tabKey={tab.key}
          >
            <tab.content {...nodeDetailsSidepanelContentProps} />
          </Tab>
        ))}
      </TabContainer>
    );
  }
}

const styles = StyleSheet.create({
  tabContentContainer: {
    padding: '16px',
  },
  tabTitleStyle: {
    display: 'flex',
    fontSize: '12px',
    paddingTop: '12px',
    paddingBottom: '6px',
    marginLeft: '12px',
    marginRight: '12px',
  },
  sidepanelTabContainerStyle: {
    marginTop: '-4px',
  },
  sidepanelHeaderDividerStyle: {
    borderTop: `1px solid ${Color.GRAY_300}`,
  },
  noFieldSelectedText: {
    fontSize: '16px',
    color: Color.GRAY_900,
  },
});
